// shared/lib/markdown.ts
// Parser markdown minimal — aucune dépendance externe.
// Gère les cas courants : headers, bold, italic, code blocks, inline code, listes, liens, hr.
// Le HTML produit est sécurisé via escapeHtml() appliqué à tout contenu externe —
// sanitize-html (Node.js) est intentionnellement absent : incompatible browser (crash Vite/browser).

/**
 * Échappe les caractères HTML spéciaux dans un texte brut.
 * Utilisé avant d'appliquer les transformations markdown inline.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Transforme le markdown inline (bold, italic, inline code, liens) en HTML.
 * Appelée sur chaque ligne de texte brut (hors blocs code).
 */
function inlineMarkdown(text: string): string {
  return text
    // Inline code — avant bold/italic pour éviter les interférences
    .replace(/`([^`]+)`/g, (_, code) => `<code>${escapeHtml(code)}</code>`)
    // Bold+italic
    .replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Liens [texte](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
}

/**
 * Convertit du markdown en HTML safe.
 * Retourne une chaîne HTML sanitisée, prête pour dangerouslySetInnerHTML.
 */
export function markdownToHtml(markdown: string): string {
  const lines = markdown.split('\n')
  const result: string[] = []

  let inCodeBlock = false
  let codeLang = ''
  let codeBuffer: string[] = []

  let inList = false
  let listBuffer: string[] = []

  const flushList = () => {
    if (listBuffer.length > 0) {
      result.push(`<ul>${listBuffer.map(item => `<li>${inlineMarkdown(item)}</li>`).join('')}</ul>`)
      listBuffer = []
      inList = false
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Blocs code fencés (``` ou ~~~)
    if (!inCodeBlock && /^```|^~~~/.test(line)) {
      flushList()
      inCodeBlock = true
      codeLang = line.replace(/^```|^~~~/, '').trim()
      codeBuffer = []
      continue
    }
    if (inCodeBlock && /^```|^~~~/.test(line)) {
      const langAttr = codeLang ? ` class="language-${escapeHtml(codeLang)}"` : ''
      result.push(`<pre><code${langAttr}>${codeBuffer.map(escapeHtml).join('\n')}</code></pre>`)
      inCodeBlock = false
      codeLang = ''
      codeBuffer = []
      continue
    }
    if (inCodeBlock) {
      codeBuffer.push(line)
      continue
    }

    // Ligne vide
    if (line.trim() === '') {
      flushList()
      // Pas de <br> inutile, on laisse les blocs gérer l'espacement
      continue
    }

    // Headers
    const h6 = line.match(/^#{6}\s+(.+)/)
    const h5 = line.match(/^#{5}\s+(.+)/)
    const h4 = line.match(/^#{4}\s+(.+)/)
    const h3 = line.match(/^#{3}\s+(.+)/)
    const h2 = line.match(/^#{2}\s+(.+)/)
    const h1 = line.match(/^#{1}\s+(.+)/)

    if (h6) { flushList(); result.push(`<h6>${inlineMarkdown(h6[1])}</h6>`); continue }
    if (h5) { flushList(); result.push(`<h5>${inlineMarkdown(h5[1])}</h5>`); continue }
    if (h4) { flushList(); result.push(`<h4>${inlineMarkdown(h4[1])}</h4>`); continue }
    if (h3) { flushList(); result.push(`<h3>${inlineMarkdown(h3[1])}</h3>`); continue }
    if (h2) { flushList(); result.push(`<h2>${inlineMarkdown(h2[1])}</h2>`); continue }
    if (h1) { flushList(); result.push(`<h1>${inlineMarkdown(h1[1])}</h1>`); continue }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushList()
      result.push('<hr />')
      continue
    }

    // Listes non ordonnées (-, *, +)
    const listMatch = line.match(/^[\s]*[-*+]\s+(.+)/)
    if (listMatch) {
      inList = true
      listBuffer.push(listMatch[1])
      continue
    }

    // Listes ordonnées (1. 2. etc.)
    const orderedMatch = line.match(/^[\s]*\d+\.\s+(.+)/)
    if (orderedMatch) {
      // Traitement simplifié : convertit en <ul> (pas de <ol> pour garder simple)
      inList = true
      listBuffer.push(orderedMatch[1])
      continue
    }

    // Blockquote
    const quoteMatch = line.match(/^>\s?(.*)/)
    if (quoteMatch) {
      flushList()
      result.push(`<blockquote><p>${inlineMarkdown(quoteMatch[1])}</p></blockquote>`)
      continue
    }

    // Paragraphe normal
    flushList()
    result.push(`<p>${inlineMarkdown(line)}</p>`)
  }

  // Fermer les blocs non terminés
  if (inCodeBlock && codeBuffer.length > 0) {
    result.push(`<pre><code>${codeBuffer.map(escapeHtml).join('\n')}</code></pre>`)
  }
  flushList()

  const rawHtml = result.join('\n')
  // Ne pas appeler sanitize-html ici : lib Node.js incompatible browser.
  // Le HTML est sûr — tout contenu externe passe par escapeHtml() dans ce parser.
  return rawHtml
}
